/**
 * LifeFunctionSystem - Simulates the 7 essential life functions
 * Handles triggering, observing, and visualizing life processes in cells
 */

import Phaser from 'phaser';

/**
 * The 7 essential life functions
 */
export enum LifeFunction {
  METABOLISM = 'metabolism',
  REPRODUCTION = 'reproduction',
  RESPONSE_TO_STIMULI = 'response',
  HOMEOSTASIS = 'homeostasis',
  GROWTH = 'growth',
  WASTE_REMOVAL = 'waste_removal',
  GAS_EXCHANGE = 'gas_exchange',
}

/**
 * Types of stimuli that cells can respond to
 */
export enum StimulusType {
  LIGHT = 'light',
  CHEMICAL = 'chemical',
  TEMPERATURE = 'temperature',
  TOUCH = 'touch',
  FOOD = 'food',
}

/**
 * Cell simulation state for life function tracking
 */
export interface CellSimulationState {
  /** Energy level (0-100) for metabolism */
  energy: number;
  /** Current size relative to base (1.0 = normal) */
  size: number;
  /** Waste accumulation level (0-100) */
  wasteLevel: number;
  /** Oxygen level (0-100) */
  oxygenLevel: number;
  /** CO2 level (0-100) */
  co2Level: number;
  /** Internal temperature relative to optimal */
  temperature: number;
  /** Whether the cell can currently reproduce */
  canReproduce: boolean;
  /** Current stimulus the cell is responding to */
  currentStimulus: StimulusType | null;
  /** Whether cell is currently dividing */
  isDividing: boolean;
  /** Division progress (0-1) */
  divisionProgress: number;
}

/**
 * Event emitted when a life function occurs
 */
export interface LifeFunctionEvent {
  function: LifeFunction;
  cellId: string;
  data: Record<string, unknown>;
  timestamp: number;
}

/**
 * Configuration for the LifeFunctionSystem
 */
export interface LifeFunctionSystemConfig {
  /** Base metabolism rate (energy consumed per second) */
  metabolismRate?: number;
  /** Growth rate multiplier */
  growthRate?: number;
  /** Time for cell division (ms) */
  divisionDuration?: number;
  /** Homeostasis correction rate */
  homeostasisRate?: number;
}

const DEFAULT_CONFIG: Required<LifeFunctionSystemConfig> = {
  metabolismRate: 2,
  growthRate: 0.01,
  divisionDuration: 5000,
  homeostasisRate: 0.05,
};

/**
 * Creates a default cell simulation state
 */
function createDefaultState(): CellSimulationState {
  return {
    energy: 75,
    size: 1.0,
    wasteLevel: 10,
    oxygenLevel: 90,
    co2Level: 10,
    temperature: 0, // 0 = optimal, negative = cold, positive = hot
    canReproduce: false,
    currentStimulus: null,
    isDividing: false,
    divisionProgress: 0,
  };
}

/**
 * LifeFunctionSystem manages simulation of all 7 life functions
 */
export class LifeFunctionSystem {
  private scene: Phaser.Scene;
  private config: Required<LifeFunctionSystemConfig>;
  private cellStates: Map<string, CellSimulationState> = new Map();
  private activeProcesses: Map<string, Set<LifeFunction>> = new Map();
  private divisionTimers: Map<string, Phaser.Time.TimerEvent> = new Map();
  private isRunning = false;

  constructor(scene: Phaser.Scene, config: LifeFunctionSystemConfig = {}) {
    this.scene = scene;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Register a cell for life function simulation
   */
  registerCell(cellId: string, initialState?: Partial<CellSimulationState>): void {
    const state = { ...createDefaultState(), ...initialState };
    this.cellStates.set(cellId, state);
    this.activeProcesses.set(cellId, new Set());
  }

  /**
   * Unregister a cell from simulation
   */
  unregisterCell(cellId: string): void {
    this.cellStates.delete(cellId);
    this.activeProcesses.delete(cellId);

    const timer = this.divisionTimers.get(cellId);
    if (timer) {
      timer.destroy();
      this.divisionTimers.delete(cellId);
    }
  }

  /**
   * Get the current state of a cell
   */
  getCellState(cellId: string): CellSimulationState | undefined {
    return this.cellStates.get(cellId);
  }

  /**
   * Start the simulation loop
   */
  start(): void {
    this.isRunning = true;
  }

  /**
   * Stop the simulation loop
   */
  stop(): void {
    this.isRunning = false;
  }

  /**
   * Update simulation (call from scene update)
   */
  update(delta: number): void {
    if (!this.isRunning) return;

    const deltaSeconds = delta / 1000;

    for (const [cellId, state] of this.cellStates) {
      this.updateMetabolism(cellId, state, deltaSeconds);
      this.updateHomeostasis(cellId, state, deltaSeconds);
      this.updateGrowth(cellId, state, deltaSeconds);
      this.updateGasExchange(cellId, state, deltaSeconds);
      this.updateWasteRemoval(cellId, state, deltaSeconds);
      this.checkReproductionReadiness(cellId, state);
    }
  }

  /**
   * Trigger a specific life function on a cell
   */
  triggerFunction(cellId: string, func: LifeFunction, params?: Record<string, unknown>): boolean {
    const state = this.cellStates.get(cellId);
    if (!state) return false;

    switch (func) {
      case LifeFunction.METABOLISM:
        return this.triggerMetabolism(cellId, state, params);
      case LifeFunction.REPRODUCTION:
        return this.triggerReproduction(cellId, state);
      case LifeFunction.RESPONSE_TO_STIMULI:
        return this.triggerResponse(cellId, state, params);
      case LifeFunction.HOMEOSTASIS:
        return this.triggerHomeostasis(cellId, state, params);
      case LifeFunction.GROWTH:
        return this.triggerGrowth(cellId, state);
      case LifeFunction.WASTE_REMOVAL:
        return this.triggerWasteRemoval(cellId, state);
      case LifeFunction.GAS_EXCHANGE:
        return this.triggerGasExchange(cellId, state);
      default:
        return false;
    }
  }

  /**
   * Apply a stimulus to a cell
   */
  applyStimulus(cellId: string, stimulus: StimulusType): void {
    const state = this.cellStates.get(cellId);
    if (!state) return;

    state.currentStimulus = stimulus;
    this.triggerFunction(cellId, LifeFunction.RESPONSE_TO_STIMULI, { stimulus });
  }

  /**
   * Feed a cell (add energy)
   */
  feedCell(cellId: string, amount: number = 20): void {
    const state = this.cellStates.get(cellId);
    if (!state) return;

    state.energy = Math.min(100, state.energy + amount);
    this.emitEvent(cellId, LifeFunction.METABOLISM, { type: 'feed', amount });
  }

  /**
   * Change cell temperature (for homeostasis)
   */
  changeTemperature(cellId: string, delta: number): void {
    const state = this.cellStates.get(cellId);
    if (!state) return;

    state.temperature = Phaser.Math.Clamp(state.temperature + delta, -50, 50);
    this.emitEvent(cellId, LifeFunction.HOMEOSTASIS, { type: 'temperature_change', delta });
  }

  // ============================================================================
  // METABOLISM
  // ============================================================================

  private updateMetabolism(cellId: string, state: CellSimulationState, deltaSeconds: number): void {
    // Cells constantly consume energy
    const energyConsumed = this.config.metabolismRate * deltaSeconds;
    state.energy = Math.max(0, state.energy - energyConsumed);

    // Metabolism produces waste
    state.wasteLevel = Math.min(100, state.wasteLevel + energyConsumed * 0.5);

    // Metabolism produces CO2 and consumes O2
    state.oxygenLevel = Math.max(0, state.oxygenLevel - energyConsumed * 0.3);
    state.co2Level = Math.min(100, state.co2Level + energyConsumed * 0.3);

    // Emit metabolism event periodically
    if (Math.random() < deltaSeconds * 0.5) {
      this.emitEvent(cellId, LifeFunction.METABOLISM, {
        energy: state.energy,
        type: 'ongoing'
      });
    }
  }

  private triggerMetabolism(cellId: string, state: CellSimulationState, params?: Record<string, unknown>): boolean {
    const amount = (params?.amount as number) ?? 10;
    state.energy = Math.min(100, state.energy + amount);

    this.emitEvent(cellId, LifeFunction.METABOLISM, {
      type: 'triggered',
      energyGained: amount,
      newEnergy: state.energy
    });

    return true;
  }

  // ============================================================================
  // REPRODUCTION (Cell Division)
  // ============================================================================

  private checkReproductionReadiness(_cellId: string, state: CellSimulationState): void {
    // Cell can reproduce if: energy > 80, size >= 1.5, not already dividing
    state.canReproduce =
      state.energy >= 80 &&
      state.size >= 1.5 &&
      !state.isDividing &&
      state.wasteLevel < 50;
  }

  private triggerReproduction(cellId: string, state: CellSimulationState): boolean {
    if (!state.canReproduce || state.isDividing) {
      return false;
    }

    state.isDividing = true;
    state.divisionProgress = 0;
    state.canReproduce = false;

    this.emitEvent(cellId, LifeFunction.REPRODUCTION, {
      type: 'division_start',
      phase: 'interphase'
    });

    // Start division timer
    const duration = this.config.divisionDuration;
    const phases = ['interphase', 'prophase', 'metaphase', 'anaphase', 'telophase', 'cytokinesis'];
    const phaseTime = duration / phases.length;

    let currentPhase = 0;

    const timer = this.scene.time.addEvent({
      delay: phaseTime,
      repeat: phases.length - 1,
      callback: () => {
        currentPhase++;
        state.divisionProgress = currentPhase / phases.length;

        if (currentPhase < phases.length) {
          this.emitEvent(cellId, LifeFunction.REPRODUCTION, {
            type: 'division_progress',
            phase: phases[currentPhase],
            progress: state.divisionProgress
          });
        }

        if (currentPhase >= phases.length - 1) {
          // Division complete
          state.isDividing = false;
          state.divisionProgress = 0;
          state.size = 1.0; // Reset to normal size
          state.energy = state.energy / 2; // Energy split between cells

          this.emitEvent(cellId, LifeFunction.REPRODUCTION, {
            type: 'division_complete',
            newCellId: `${cellId}_daughter`
          });
        }
      }
    });

    this.divisionTimers.set(cellId, timer);
    return true;
  }

  // ============================================================================
  // RESPONSE TO STIMULI
  // ============================================================================

  private triggerResponse(cellId: string, state: CellSimulationState, params?: Record<string, unknown>): boolean {
    const stimulus = (params?.stimulus as StimulusType) ?? state.currentStimulus;
    if (!stimulus) return false;

    let responseType: string;
    let direction: number = 0; // -1 = away, 0 = neutral, 1 = toward

    switch (stimulus) {
      case StimulusType.LIGHT:
        responseType = 'phototaxis';
        direction = 1; // Move toward light (positive phototaxis)
        break;
      case StimulusType.CHEMICAL:
        responseType = 'chemotaxis';
        direction = Math.random() > 0.5 ? 1 : -1; // Depends on chemical
        break;
      case StimulusType.TEMPERATURE:
        responseType = 'thermotaxis';
        direction = state.temperature > 0 ? -1 : 1; // Move toward optimal
        break;
      case StimulusType.TOUCH:
        responseType = 'thigmotaxis';
        direction = -1; // Move away from touch
        break;
      case StimulusType.FOOD:
        responseType = 'chemotaxis';
        direction = 1; // Move toward food
        break;
      default:
        return false;
    }

    this.emitEvent(cellId, LifeFunction.RESPONSE_TO_STIMULI, {
      stimulus,
      responseType,
      direction,
    });

    // Clear stimulus after response
    state.currentStimulus = null;
    return true;
  }

  // ============================================================================
  // HOMEOSTASIS
  // ============================================================================

  private updateHomeostasis(_cellId: string, state: CellSimulationState, deltaSeconds: number): void {
    // Gradually return temperature to optimal (0)
    if (state.temperature !== 0) {
      const correction = this.config.homeostasisRate * deltaSeconds * 10;
      if (state.temperature > 0) {
        state.temperature = Math.max(0, state.temperature - correction);
      } else {
        state.temperature = Math.min(0, state.temperature + correction);
      }
    }
  }

  private triggerHomeostasis(cellId: string, state: CellSimulationState, params?: Record<string, unknown>): boolean {
    const parameter = params?.parameter as string ?? 'temperature';

    this.emitEvent(cellId, LifeFunction.HOMEOSTASIS, {
      type: 'regulation',
      parameter,
      currentValue: parameter === 'temperature' ? state.temperature : 0,
      targetValue: 0,
    });

    return true;
  }

  // ============================================================================
  // GROWTH
  // ============================================================================

  private updateGrowth(_cellId: string, state: CellSimulationState, deltaSeconds: number): void {
    // Cell grows when it has enough energy
    if (state.energy > 60 && state.size < 2.0 && !state.isDividing) {
      const growthAmount = this.config.growthRate * deltaSeconds * (state.energy / 100);
      state.size = Math.min(2.0, state.size + growthAmount);
    }
  }

  private triggerGrowth(cellId: string, state: CellSimulationState): boolean {
    if (state.energy < 30) return false;

    const growthAmount = 0.1;
    state.size = Math.min(2.0, state.size + growthAmount);
    state.energy -= 10; // Growth costs energy

    this.emitEvent(cellId, LifeFunction.GROWTH, {
      newSize: state.size,
      energyCost: 10,
    });

    return true;
  }

  // ============================================================================
  // WASTE REMOVAL
  // ============================================================================

  private updateWasteRemoval(_cellId: string, state: CellSimulationState, deltaSeconds: number): void {
    // Automatic slow waste removal
    if (state.wasteLevel > 0) {
      state.wasteLevel = Math.max(0, state.wasteLevel - deltaSeconds * 0.5);
    }
  }

  private triggerWasteRemoval(cellId: string, state: CellSimulationState): boolean {
    const wasteRemoved = Math.min(state.wasteLevel, 20);
    state.wasteLevel -= wasteRemoved;

    this.emitEvent(cellId, LifeFunction.WASTE_REMOVAL, {
      wasteRemoved,
      remainingWaste: state.wasteLevel,
    });

    return wasteRemoved > 0;
  }

  // ============================================================================
  // GAS EXCHANGE
  // ============================================================================

  private updateGasExchange(_cellId: string, state: CellSimulationState, deltaSeconds: number): void {
    // Automatic gas exchange (respiration)
    // O2 in, CO2 out
    const exchangeRate = deltaSeconds * 2;

    if (state.oxygenLevel < 100) {
      state.oxygenLevel = Math.min(100, state.oxygenLevel + exchangeRate);
    }
    if (state.co2Level > 0) {
      state.co2Level = Math.max(0, state.co2Level - exchangeRate);
    }
  }

  private triggerGasExchange(cellId: string, state: CellSimulationState): boolean {
    const o2Gained = Math.min(100 - state.oxygenLevel, 15);
    const co2Released = Math.min(state.co2Level, 15);

    state.oxygenLevel += o2Gained;
    state.co2Level -= co2Released;

    this.emitEvent(cellId, LifeFunction.GAS_EXCHANGE, {
      oxygenGained: o2Gained,
      co2Released,
      oxygenLevel: state.oxygenLevel,
      co2Level: state.co2Level,
    });

    return true;
  }

  // ============================================================================
  // EVENTS
  // ============================================================================

  private emitEvent(cellId: string, func: LifeFunction, data: Record<string, unknown>): void {
    const event: LifeFunctionEvent = {
      function: func,
      cellId,
      data,
      timestamp: Date.now(),
    };

    this.scene.events.emit('lifeFunction:event', event);
    this.scene.events.emit(`lifeFunction:${func}`, event);
  }

  /**
   * Get all active life functions for a cell
   */
  getActiveProcesses(cellId: string): LifeFunction[] {
    const processes = this.activeProcesses.get(cellId);
    return processes ? Array.from(processes) : [];
  }

  /**
   * Check if a specific life function is active for a cell
   */
  isFunctionActive(cellId: string, func: LifeFunction): boolean {
    const processes = this.activeProcesses.get(cellId);
    return processes?.has(func) ?? false;
  }

  /**
   * Get human-readable description of a life function
   */
  static getFunctionDescription(func: LifeFunction): string {
    const descriptions: Record<LifeFunction, string> = {
      [LifeFunction.METABOLISM]: 'The process of converting nutrients into energy',
      [LifeFunction.REPRODUCTION]: 'The ability to create new cells through division',
      [LifeFunction.RESPONSE_TO_STIMULI]: 'Reacting to changes in the environment',
      [LifeFunction.HOMEOSTASIS]: 'Maintaining stable internal conditions',
      [LifeFunction.GROWTH]: 'Increasing in size by building new cellular material',
      [LifeFunction.WASTE_REMOVAL]: 'Removing metabolic waste products from the cell',
      [LifeFunction.GAS_EXCHANGE]: 'Taking in oxygen and releasing carbon dioxide',
    };
    return descriptions[func];
  }

  /**
   * Get display name for a life function
   */
  static getFunctionName(func: LifeFunction): string {
    const names: Record<LifeFunction, string> = {
      [LifeFunction.METABOLISM]: 'Metabolism',
      [LifeFunction.REPRODUCTION]: 'Reproduction',
      [LifeFunction.RESPONSE_TO_STIMULI]: 'Response to Stimuli',
      [LifeFunction.HOMEOSTASIS]: 'Homeostasis',
      [LifeFunction.GROWTH]: 'Growth',
      [LifeFunction.WASTE_REMOVAL]: 'Waste Removal',
      [LifeFunction.GAS_EXCHANGE]: 'Gas Exchange',
    };
    return names[func];
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.stop();

    for (const timer of this.divisionTimers.values()) {
      timer.destroy();
    }

    this.cellStates.clear();
    this.activeProcesses.clear();
    this.divisionTimers.clear();
  }
}
